import uniqBy from 'lodash/uniqBy'
import { TFunction } from 'i18next'
import { Form, Formik, Field } from 'formik'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useTw } from '../../../../../utils/Styles'
import { MemberUser } from '../../../../../api/types'
import { Avatars } from '../../../../../utils/Avatars'
import { useProfile } from '../../../../../utils/Profile'
import { ChevronRightIcon } from '@heroicons/react/outline'
import { isDark, localizeString } from '../../../../../utils'
import { SubmitListener } from '../../../../../utils/SubmitListener'
import Wrapper, { LoadingWrapper } from '../../../../../utils/Wrapper'
import {
  KnowledgeBaseQuery,
  useKnowledgeBaseQuery,
} from '../KnowledgeBase.generated'

type Props = {
  category: KnowledgeBaseQuery['Profile']['knowledgeBase']['categories'][0]
}

function ArticlesLength({ length, t }: { length: number; t: TFunction }) {
  const tw = useTw()
  return (
    <span className={tw`font-bold text-neutral-400 leading-5`}>
      {length > 0 ? (
        <>
          {`${length} ${t('knowledge_base.articles.articles_count.quantity')}`}
        </>
      ) : (
        <>{t('knowledge_base.articles.articles_count.empty')}</>
      )}
    </span>
  )
}

function UserText({ users = [], t }: { users: MemberUser[]; t: TFunction }) {
  const tw = useTw()
  if (!users.length) return <span className={tw`h-5`} />

  return (
    <span className={tw`text-sm text-neutral-350`}>
      {t(
        users.length === 1
          ? 'knowledge_base.creators.creator'
          : users.length === 2
          ? 'knowledge_base.creators.creator_two'
          : 'knowledge_base.creators.creator_plural',
        {
          postProcess: 'interval',
          count: 2,
          countlt2: users.length - 2,
          users,
        }
      )}
    </span>
  )
}

function Category({ category }: Props) {
  const tw = useTw()
  const navigate = useNavigate()
  const [t] = useTranslation('common')
  const users = uniqBy(
    category.articles.map((_) => _.createdBy),
    '_id'
  )

  return (
    <div
      className={tw`rounded shadow-md ${
        !category.logoURL && 'border-t-2 border-foreground'
      }`}
    >
      {category.logoURL && (
        <img
          src={category.logoURL}
          className={tw`rounded-t border border-neutral-200`}
        />
      )}
      <div className={tw`p-4 space-y-2`}>
        <button
          className={tw`cursor-pointer text-foreground`}
          onClick={() => navigate(`/kb/${category._id}`)}
        >
          {localizeString(category.title)}
        </button>
        <p
          title={localizeString(category.subtitle)}
          className={tw`line-clamp-2 text-neutral-400`}
        >
          {localizeString(category.subtitle)}
        </p>
        <div className={tw`flex justify-between items-center pt-1`}>
          <div className={tw`flex flex-row select-none`}>
            <div className={tw`flex-shrink-0`}>
              <Avatars users={users} />
            </div>
            <p className={tw`flex flex-col ml-2`}>
              <ArticlesLength length={category.articles.length} t={t} />
              <UserText users={users} t={t} />
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export function Categories() {
  const tw = useTw()
  const profile = useProfile()
  const navigate = useNavigate()
  const [t] = useTranslation('common')
  const { data, loading } = useKnowledgeBaseQuery()

  if (loading) return <LoadingWrapper title={t('knowledge_base.title')} />

  return (
    <Wrapper
      title={t('knowledge_base.title')}
      subtitle={t('knowledge_base.categories.subtitle')}
    >
      <Formik
        initialValues={{ search: '' }}
        onSubmit={(values) =>
          values.search &&
          navigate('/kb-search', { state: { search: values.search } })
        }
      >
        <Form className={tw`flex px-4 mt-4 h-14 overflow-y-scroll`}>
          <Field
            name="search"
            type="text"
            placeholder={t('home.kb.search_placeholder')}
            className={tw`select-none my-2 w-full rounded-l border-r-0 focus:ring-foreground focus:border-foreground outline-none`}
          />
          <button
            type="submit"
            className={tw`leading-6 px-2 my-2 bg-foreground rounded-r`}
          >
            <ChevronRightIcon
              className={tw`h-6 w-6 ${
                isDark(profile.foreground) ? 'text-white' : 'text-black'
              }`}
            />
          </button>
          <SubmitListener />
        </Form>
      </Formik>
      <div className={tw`my-4 space-y-4 px-4`}>
        {data?.Profile?.knowledgeBase?.categories
          ?.filter((_) => _?.articles?.length)
          .map((_) => (
            <Category key={_._id} category={_} />
          ))}
      </div>
    </Wrapper>
  )
}
